import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import QuoteForm from './components/QuoteForm';
import QuotesTable from './components/QuotesTable';
import QuoteDetailModal from './components/QuoteDetailModal';
import ConvertQuoteModal from './components/ConvertQuoteModal';
import PriceListModal from './components/PriceListModal';
import PriceListTable from './components/PriceListTable';
import EditQuoteModal from './components/EditQuoteModal';
import QuoteApprovalWorkflow from './components/QuoteApprovalWorkflow';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';


const QuoteManagement = () => {
  const [activeTab, setActiveTab] = useState('create');
  const [quotes, setQuotes] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedPriceList, setSelectedPriceList] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showPriceListModal, setShowPriceListModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showApprovalWorkflow, setShowApprovalWorkflow] = useState(false);
  const [approvalQuote, setApprovalQuote] = useState(null);

  useEffect(() => {
    if (activeTab === 'view') {
      fetchQuotes();
    } else if (activeTab === 'pricing') {
      fetchPriceLists();
    }
  }, [activeTab]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('quotes')
        ?.select(`
          *,
          customer:customers(*),
          salesperson:personnel!quotes_salesperson_id_fkey(*),
          status:quote_statuses(*),
          quote_products(*)
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      setQuotes(data || []);
    } catch (error) {
      console.error('Error fetching quotes:', error?.message);
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceLists = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('customer_price_lists')
        ?.select(`
          *,
          customer:customers(*),
          price_list_items(*)
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      setPriceLists(data || []);
    } catch (error) {
      console.error('Error fetching price lists:', error?.message);
      setPriceLists([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuote = async (quoteData) => {
    try {
      // Insert quote
      const { data: newQuote, error: quoteError } = await supabase
        ?.from('quotes')
        ?.insert([{
          customer_id: quoteData?.customerId,
          salesperson_id: quoteData?.salespersonId,
          status_id: quoteData?.statusId,
          notes: quoteData?.notes,
          expected_service_date: quoteData?.expectedServiceDate
        }])
        ?.select()
        ?.single();

      if (quoteError) throw quoteError;

      // Insert quote products
      if (quoteData?.products?.length > 0) {
        const quoteProducts = quoteData?.products?.map(product => ({
          quote_id: newQuote?.id,
          product_id: product?.productId || null,
          name: product?.name,
          quantity: product?.quantity,
          price: product?.price
        }));

        const { error: productsError } = await supabase
          ?.from('quote_products')
          ?.insert(quoteProducts);

        if (productsError) throw productsError;
      }

      setSuccessMessage(`Quote #${newQuote?.quote_number} created successfully!`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      // Fetch updated quotes before switching tabs
      await fetchQuotes();
      
      // Switch to view tab after data is loaded
      setActiveTab('view');
    } catch (error) {
      console.error('Error creating quote:', error?.message);
      const errorMessage = error?.message || 'Failed to create quote. Please try again.';
      alert(`Error: ${errorMessage}`);
      throw error; // Re-throw to let QuoteForm handle it
    }
  };

  const handleSendQuote = async (quoteId) => {
    try {
      // Get sent status ID
      const { data: sentStatus, error: statusError } = await supabase
        ?.from('quote_statuses')
        ?.select('id')
        ?.eq('name', 'Sent')
        ?.single();

      if (statusError || !sentStatus) throw new Error('Sent status not found');

      // Update quote status
      const { error } = await supabase
        ?.from('quotes')
        ?.update({ status_id: sentStatus?.id })
        ?.eq('id', quoteId);

      if (error) throw error;

      // Send email notification via edge function
      try {
        const quote = quotes?.find(q => q?.id === quoteId);
        if (quote?.customer?.email) {
          await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-quote-notification`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({
              type: 'quote_sent',
              recipientEmail: quote?.customer?.email,
              recipientName: quote?.customer?.name,
              quoteNumber: quote?.quote_number,
              totalAmount: quote?.total_amount,
              salespersonName: quote?.salesperson?.name
            })
          });
        }
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
      }

      setSuccessMessage('Quote sent to customer successfully!');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      await fetchQuotes();
    } catch (error) {
      console.error('Error sending quote:', error?.message);
      const errorMessage = error?.message || 'Failed to send quote. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleApproveQuote = async (quoteId) => {
    try {
      // Get approved status ID
      const { data: approvedStatus, error: statusError } = await supabase
        ?.from('quote_statuses')
        ?.select('id')
        ?.eq('name', 'Approved')
        ?.single();

      if (statusError || !approvedStatus) throw new Error('Approved status not found');

      // Update quote status and approval date
      const { error } = await supabase
        ?.from('quotes')
        ?.update({ 
          status_id: approvedStatus?.id,
          approval_date: new Date()?.toISOString()
        })
        ?.eq('id', quoteId);

      if (error) throw error;

      // Send approval notification via edge function
      try {
        const quote = quotes?.find(q => q?.id === quoteId);
        if (quote?.salesperson?.email) {
          await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-quote-notification`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({
              type: 'quote_approved',
              recipientEmail: quote?.salesperson?.email,
              recipientName: quote?.salesperson?.name,
              quoteNumber: quote?.quote_number,
              customerName: quote?.customer?.name,
              totalAmount: quote?.total_amount
            })
          });
        }
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
      }

      setSuccessMessage('Quote approved successfully!');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      await fetchQuotes();
    } catch (error) {
      console.error('Error approving quote:', error?.message);
      const errorMessage = error?.message || 'Failed to approve quote. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleEditQuote = async (quoteId, updatedData) => {
    try {
      // Update quote
      const { error: quoteError } = await supabase
        ?.from('quotes')
        ?.update({
          customer_id: updatedData?.customerId,
          salesperson_id: updatedData?.salespersonId,
          expected_service_date: updatedData?.expectedServiceDate,
          notes: updatedData?.notes
        })
        ?.eq('id', quoteId);

      if (quoteError) throw quoteError;

      // Delete existing quote products
      const { error: deleteError } = await supabase
        ?.from('quote_products')
        ?.delete()
        ?.eq('quote_id', quoteId);

      if (deleteError) throw deleteError;

      // Insert updated quote products
      if (updatedData?.products?.length > 0) {
        const quoteProducts = updatedData?.products?.map(product => ({
          quote_id: quoteId,
          product_id: product?.productId || null,
          name: product?.name,
          quantity: product?.quantity,
          price: product?.price
        }));

        const { error: productsError } = await supabase
          ?.from('quote_products')
          ?.insert(quoteProducts);

        if (productsError) throw productsError;
      }

      setSuccessMessage('Quote updated successfully!');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      await fetchQuotes();
    } catch (error) {
      console.error('Error updating quote:', error?.message);
      const errorMessage = error?.message || 'Failed to update quote. Please try again.';
      alert(`Error: ${errorMessage}`);
      throw error;
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!confirm('Are you sure you want to delete this quote? This action cannot be undone.')) return;

    try {
      // Delete quote products first (foreign key constraint)
      const { error: productsError } = await supabase
        ?.from('quote_products')
        ?.delete()
        ?.eq('quote_id', quoteId);

      if (productsError) throw productsError;

      // Delete quote
      const { error: quoteError } = await supabase
        ?.from('quotes')
        ?.delete()
        ?.eq('id', quoteId);

      if (quoteError) throw quoteError;

      setSuccessMessage('Quote deleted successfully!');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      await fetchQuotes();
    } catch (error) {
      console.error('Error deleting quote:', error?.message);
      const errorMessage = error?.message || 'Failed to delete quote. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleConvertToTicket = async (conversionData) => {
    try {
      // Get pending status ID
      const { data: pendingStatus } = await supabase
        ?.from('ticket_statuses')
        ?.select('id')
        ?.eq('name', 'Pending')
        ?.single();

      if (!pendingStatus) throw new Error('Pending status not found');

      const quote = selectedQuote;

      // Create ticket with Pending status — no technicians or schedule assigned yet
      const { data: newTicket, error: ticketError } = await supabase
        ?.from('tickets')
        ?.insert([{
          customer_id: quote?.customer_id,
          service_type_id: conversionData?.serviceTypeId,
          priority_id: conversionData?.priorityId,
          status_id: pendingStatus?.id,
          salesperson_id: quote?.salesperson_id,
          description: `Converted from Quote ${quote?.quote_number}\n\n${conversionData?.notes || ''}`
        }])
        ?.select()
        ?.single();

      if (ticketError) throw ticketError;

      // Copy quote products to ticket products
      if (quote?.quote_products?.length > 0) {
        const ticketProducts = quote?.quote_products?.map(product => ({
          ticket_id: newTicket?.id,
          name: product?.name,
          quantity: product?.quantity,
          price: product?.price
        }));

        const { error: productsError } = await supabase
          ?.from('ticket_products')
          ?.insert(ticketProducts);

        if (productsError) throw productsError;
      }

      // Update quote status to Converted
      const { data: convertedStatus } = await supabase
        ?.from('quote_statuses')
        ?.select('id')
        ?.eq('name', 'Converted')
        ?.single();

      if (convertedStatus) {
        await supabase
          ?.from('quotes')
          ?.update({ status_id: convertedStatus?.id })
          ?.eq('id', quote?.id);
      }

      setSuccessMessage(`Quote converted to Ticket ${newTicket?.ticket_number} — status set to Pending. Assign technicians and schedule when the customer calls.`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);

      setShowConvertModal(false);
      setSelectedQuote(null);
      await fetchQuotes();
    } catch (error) {
      console.error('Error converting quote to ticket:', error?.message);
      throw new Error(error?.message || 'Failed to convert quote to ticket. Please try again.');
    }
  };

  const handleCreatePriceList = async (priceListData, priceListId = null) => {
    try {
      if (priceListId) {
        // Update existing price list
        const { error: updateError } = await supabase
          ?.from('customer_price_lists')
          ?.update({
            name: priceListData?.name,
            description: priceListData?.description,
            valid_from: priceListData?.validFrom,
            valid_until: priceListData?.validUntil || null
          })
          ?.eq('id', priceListId);

        if (updateError) throw updateError;

        // Delete existing items
        await supabase
          ?.from('price_list_items')
          ?.delete()
          ?.eq('price_list_id', priceListId);

        // Insert new items
        if (priceListData?.items?.length > 0) {
          const items = priceListData?.items?.map(item => ({
            price_list_id: priceListId,
            product_id: item?.productId,
            discount_type: item?.discountType,
            discount_value: item?.discountValue,
            custom_price: item?.customPrice || null,
            notes: item?.notes
          }));

          const { error: itemsError } = await supabase
            ?.from('price_list_items')
            ?.insert(items);

          if (itemsError) throw itemsError;
        }

        setSuccessMessage('Price list updated successfully!');
      } else {
        // Create new price list
        const { data: newPriceList, error: priceListError } = await supabase
          ?.from('customer_price_lists')
          ?.insert([{
            name: priceListData?.name,
            customer_id: priceListData?.customerId,
            description: priceListData?.description,
            valid_from: priceListData?.validFrom,
            valid_until: priceListData?.validUntil || null,
            is_active: true
          }])
          ?.select()
          ?.single();

        if (priceListError) throw priceListError;

        // Insert price list items
        if (priceListData?.items?.length > 0) {
          const items = priceListData?.items?.map(item => ({
            price_list_id: newPriceList?.id,
            product_id: item?.productId,
            discount_type: item?.discountType,
            discount_value: item?.discountValue,
            custom_price: item?.customPrice || null,
            notes: item?.notes
          }));

          const { error: itemsError } = await supabase
            ?.from('price_list_items')
            ?.insert(items);

          if (itemsError) throw itemsError;
        }

        setSuccessMessage('Price list created successfully!');
      }

      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      setShowPriceListModal(false);
      setSelectedPriceList(null);
      fetchPriceLists();
    } catch (error) {
      console.error('Error saving price list:', error?.message);
      alert('Failed to save price list. Please try again.');
    }
  };

  const handleDeletePriceList = async (priceListId) => {
    if (!confirm('Are you sure you want to delete this price list?')) return;

    try {
      const { error } = await supabase
        ?.from('customer_price_lists')
        ?.delete()
        ?.eq('id', priceListId);

      if (error) throw error;

      setSuccessMessage('Price list deleted successfully!');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      fetchPriceLists();
    } catch (error) {
      console.error('Error deleting price list:', error?.message);
      alert('Failed to delete price list. Please try again.');
    }
  };

  const handleViewDetails = (quote) => {
    setSelectedQuote(quote);
    setShowDetailModal(true);
  };

  const handleInitiateConversion = (quote) => {
    setSelectedQuote(quote);
    setShowConvertModal(true);
  };

  const handleOpenApproval = (quote) => {
    setApprovalQuote(quote);
    setShowApprovalWorkflow(true);
  };

  const handleDuplicateQuote = async (quote) => {
    try {
      // Get draft status ID
      const { data: draftStatus, error: statusError } = await supabase
        ?.from('quote_statuses')
        ?.select('id')
        ?.eq('name', 'Draft')
        ?.single();

      if (statusError || !draftStatus) throw new Error('Draft status not found');

      // Create duplicate quote
      const { data: newQuote, error: quoteError } = await supabase
        ?.from('quotes')
        ?.insert([{
          customer_id: quote?.customer_id,
          salesperson_id: quote?.salesperson_id,
          status_id: draftStatus?.id,
          notes: quote?.notes,
          expected_service_date: quote?.expected_service_date,
        }])
        ?.select()
        ?.single();

      if (quoteError) throw quoteError;

      // Copy quote products
      if (quote?.quote_products?.length > 0) {
        const products = quote?.quote_products?.map(p => ({
          quote_id: newQuote?.id,
          product_id: p?.product_id || null,
          name: p?.name,
          quantity: p?.quantity,
          price: p?.price,
        }));
        const { error: productsError } = await supabase
          ?.from('quote_products')
          ?.insert(products);
        if (productsError) throw productsError;
      }

      setSuccessMessage(`Quote duplicated as ${newQuote?.quote_number} (Draft)`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      await fetchQuotes();
    } catch (error) {
      console.error('Error duplicating quote:', error?.message);
      alert(`Error: ${error?.message}`);
    }
  };

  const tabs = [
    { id: 'create', label: 'Create Quote', icon: 'Plus' },
    { id: 'view', label: 'View Quotes', icon: 'FileText' },
    { id: 'pricing', label: 'Customer Pricing', icon: 'Tag' }
  ];

  return (
    <>
      <Helmet>
        <title>Quote Management | Field Service Management</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="container mx-auto px-4 py-6 md:py-8">
          {showSuccessMessage && (
            <div className="mb-6 bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
              <p className="text-success font-medium">{successMessage}</p>
            </div>
          )}

          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
              Quote Management
            </h1>
            <p className="text-muted-foreground">
              Create quotes for customers, send for approval, and convert to tickets
            </p>
          </div>

          <div className="bg-card rounded-xl border border-border shadow-elevation-1 mb-6">
            <div className="flex border-b border-border">
              <button
                onClick={() => setActiveTab('create')}
                className={`flex-1 px-4 md:px-6 py-3 md:py-4 text-sm md:text-base font-medium transition-smooth ${
                  activeTab === 'create' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Icon name="FilePlus" size={18} color="currentColor" />
                  <span>Create Quote</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('view')}
                className={`flex-1 px-4 md:px-6 py-3 md:py-4 text-sm md:text-base font-medium transition-smooth ${
                  activeTab === 'view' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Icon name="FileText" size={18} color="currentColor" />
                  <span>View Quotes</span>
                </div>
              </button>
            </div>
          </div>

          {activeTab === 'create' && (
            <QuoteForm onSubmit={handleCreateQuote} />
          )}

          {activeTab === 'view' && (
            <QuotesTable
              quotes={quotes}
              loading={loading}
              onViewDetails={(quote) => {
                setSelectedQuote(quote);
                setShowDetailModal(true);
              }}
              onSendQuote={handleSendQuote}
              onApproveQuote={handleApproveQuote}
              onConvertToTicket={(quote) => {
                setSelectedQuote(quote);
                setShowConvertModal(true);
              }}
              onEditQuote={(quote) => {
                setSelectedQuote(quote);
                setShowEditModal(true);
              }}
              onDeleteQuote={handleDeleteQuote}
              onOpenApproval={handleOpenApproval}
              onDuplicateQuote={handleDuplicateQuote}
            />
          )}

          {activeTab === 'pricing' && (
            <div>
              <div className="flex justify-end mb-4">
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => {
                    setSelectedPriceList(null);
                    setShowPriceListModal(true);
                  }}
                >
                  Create Price List
                </Button>
              </div>
              <PriceListTable
                priceLists={priceLists}
                loading={loading}
                onEdit={(priceList) => {
                  setSelectedPriceList(priceList);
                  setShowPriceListModal(true);
                }}
                onDelete={handleDeletePriceList}
                onViewDetails={(priceList) => {
                  setSelectedPriceList(priceList);
                  setShowDetailModal(true);
                }}
              />
            </div>
          )}
        </main>
      </div>

      {showDetailModal && selectedQuote && (
        <QuoteDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedQuote(null);
          }}
          quote={selectedQuote}
          onOpenApproval={handleOpenApproval}
        />
      )}

      {showConvertModal && selectedQuote && (
        <ConvertQuoteModal
          isOpen={showConvertModal}
          onClose={() => {
            setShowConvertModal(false);
            setSelectedQuote(null);
          }}
          quote={selectedQuote}
          onConfirm={handleConvertToTicket}
        />
      )}

      {showPriceListModal && (
        <PriceListModal
          isOpen={showPriceListModal}
          onClose={() => {
            setShowPriceListModal(false);
            setSelectedPriceList(null);
          }}
          onSubmit={handleCreatePriceList}
          priceList={selectedPriceList}
        />
      )}

      {showEditModal && selectedQuote && (
        <EditQuoteModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedQuote(null);
          }}
          quote={selectedQuote}
          onSubmit={handleEditQuote}
        />
      )}

      {showApprovalWorkflow && approvalQuote && (
        <QuoteApprovalWorkflow
          quote={approvalQuote}
          onClose={() => {
            setShowApprovalWorkflow(false);
            setApprovalQuote(null);
          }}
          onApprovalUpdate={() => {
            fetchQuotes();
            setSuccessMessage('Approval workflow updated successfully!');
            setShowSuccessMessage(true);
            setTimeout(() => setShowSuccessMessage(false), 3000);
          }}
        />
      )}
    </>
  );
};

export default QuoteManagement;